import type { CarProfile } from '../../cars/CarProfile';
import { carPath } from '../../cars/registry';
import { navigate } from '../App';
import { isAdmin, isCarUnlocked, getAccountEmail, clearAccountData, deleteAccount } from '../../utils/access';
import { useEffect, useRef, useState } from 'preact/hooks';
import { currentSession } from '../../core/Session';
import { AccountModal } from '../components/AccountModal';
import { CarChoice } from '../components/CarChoice';
import { RestoreButton } from '../components/RestoreButton';
import { Wordmark } from '../components/Wordmark';
import { createPortal } from 'preact/compat';
import { useT, LANGS, getLangSetting, setLang, getLang, detectLang } from '../../i18n';
import { iapPrice, onIapReady } from '../../utils/iap';

export function Home({ cars, openAccount = false }: { cars: CarProfile[]; openAccount?: boolean }) {
  const t = useT();
  const [showAccount, setShowAccount] = useState(openAccount);
  const admin = isAdmin();
  // inside the phone app the drawer is titled MENU; on the web the MENU button already says so next to the ✕
  const inApp = /ROARDRIVE-App/.test(navigator.userAgent);
  const [choice, setChoice] = useState<{ car: CarProfile; rect: DOMRect } | null>(null);
  const [email, setEmail] = useState(getAccountEmail());
  const [menu, setMenuRaw] = useState(false);
  const [closing, setClosing] = useState(false);
  // open at once; on close keep the drawer mounted while it slides out so the burger and the panel move together
  const setMenu = (open: boolean) => { if (open) { setClosing(false); setMenuRaw(true); } else if (menu) { setClosing(true); setTimeout(() => { setMenuRaw(false); setClosing(false); }, 380); } };
  // phone: the car row is a horizontal swipe; a red indicator above it tracks the scroll position
  const carRow = useRef<HTMLUListElement>(null);
  const [carNav, setCarNav] = useState({ left: 0, width: 1 });
  /** One lap = the distance from a card to its own copy. NOT scrollWidth/2: ten cards carry nine gaps,
   *  not eight, so half the width lands half a gap short and the loop drifts sideways every turn. */
  const lapWidth = () => {
    const el = carRow.current;
    if (!el || el.children.length < 2) return 0;
    const first = el.children[0] as HTMLElement;
    const twin = el.children[Math.floor(el.children.length / 2)] as HTMLElement;
    return twin.offsetLeft - first.offsetLeft;
  };
  // the shelf is in the row TWICE, so the indicator tracks the position inside ONE lap — against the
  // doubled width it would never leave the left half
  const onCarScroll = () => {
    const el = carRow.current;
    if (!el) return;
    const lap = lapWidth() || el.scrollWidth || 1;
    setCarNav({ left: (el.scrollLeft % lap) / lap, width: Math.min(1, el.clientWidth / lap) });
  };
  /** while this instant is in the future the drift holds still: the reader has the row */
  const driftHold = useRef(0);
  /** the DEMO | PURCHASE glass is open over a card: the card must not drift out from under it */
  const choiceOpen = useRef(false);
  useEffect(() => { choiceOpen.current = !!choice; if (!choice) driftHold.current = Date.now() + 500; }, [choice]);
  /** THE GLASS LANDED HALF OFF THE SCREEN (Marc, 2026-10-06, iPhone). On a phone the drifting row
   *  almost never has the tapped card fully in view, and the glass took the card's rectangle as it
   *  was at the tap: a card cut by the screen edge gave a glass cut the same way, and the drift kept
   *  moving the card away underneath it. Now the row stops, slides the card fully into view, and the
   *  glass opens where the card is going to be, clamped to the screen in both directions. */
  const openChoice = (car: CarProfile, card: HTMLElement) => {
    choiceOpen.current = true;
    const r = card.getBoundingClientRect();
    let shift = 0;
    const row = carRow.current;
    if (row) {
      const box = row.getBoundingClientRect();
      const left = Math.max(box.left, 0), right = Math.min(box.right, window.innerWidth);
      if (r.width >= right - left) shift = r.left + r.width / 2 - (left + right) / 2;
      else if (r.left < left) shift = r.left - left;
      else if (r.right > right) shift = r.right - right;
      const target = Math.max(0, Math.min(row.scrollWidth - row.clientWidth, row.scrollLeft + shift));
      shift = target - row.scrollLeft;
      if (Math.abs(shift) >= 1) row.scrollTo({ left: target, behavior: 'smooth' });
    }
    const m = 8;
    const x0 = Math.max(m, r.left - shift), x1 = Math.min(window.innerWidth - m, r.right - shift);
    const y0 = Math.max(m, r.top), y1 = Math.min(window.innerHeight - m, r.bottom);
    setChoice({ car, rect: new DOMRect(x0, y0, Math.max(0, x1 - x0), Math.max(0, y1 - y0)) });
  };
  useEffect(() => { onCarScroll(); window.addEventListener('resize', onCarScroll); return () => window.removeEventListener('resize', onCarScroll); }, []);
  // A MOUSE HAD NO WAY IN (Marc, 2026-09-02: "no puedo hacer horizontal scroll en los coches"). The row
  // was built as a touch swipe and its scrollbar is hidden on purpose, so on a phone or a Tesla screen
  // you drag it and it works. On a desktop there was nothing at all: no bar, no drag handler, no arrows,
  // and a wheel only ever scrolls the page. It stayed invisible while the shelf held one car, because
  // one card never overflowed — the day three more arrived, the row became unreachable.
  useEffect(() => {
    const el = carRow.current;
    if (!el) return;
    const canScroll = () => el.scrollWidth > el.clientWidth + 1;
    // a vertical wheel over the row pushes it sideways, but ONLY while there is row left to travel: at
    // either end the gesture goes back to the page, so the reader is never trapped inside the carousel
    const onWheel = (e: WheelEvent) => {
      if (!canScroll() || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      const room = e.deltaY > 0 ? el.scrollWidth - el.clientWidth - el.scrollLeft : el.scrollLeft;
      if (room <= 1) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    // click-and-drag. Snap is switched off for the duration or it fights every pixel, and a drag of more
    // than a few pixels swallows the click that follows, so dragging across a card never opens it
    // `movedAt` is a TIMESTAMP, not a flag. A boolean here is sticky: if the release is ever missed the
    // flag stays raised and every click on the shelf is swallowed from then on, which is exactly the
    // dead carousel this replaced. A stamp expires by itself, so the worst a missed release can cost is
    // the one click that follows it.
    let down = false, startX = 0, startLeft = 0, movedAt = 0;
    const onDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || e.button !== 0 || !canScroll()) return;
      down = true; movedAt = 0; startX = e.clientX; startLeft = el.scrollLeft;
      el.style.cursor = 'grabbing';
    };
    const onMove = (e: PointerEvent) => {
      if (!down) return;
      // the belt to the capture's braces: no button held means the mouse came up while we were not
      // looking, so let go of the row instead of dragging it around after the hand has left
      if (e.buttons === 0) { end(); return; }
      driftHold.current = Date.now() + 400; // the drift stays out of the way while the hand is on it
      const dx = e.clientX - startX;
      if (!movedAt && Math.abs(dx) < 5) return;
      // CAPTURE THE POINTER, but only once this is a DRAG. Capture it on the press and the click that
      // follows a plain press is delivered to the row instead of the card (with a capture in place the
      // release targets the capturing element, so no card could ever be opened by a mouse). Capture it
      // here and the plain click is untouched, while a drag still hears its release wherever it lands:
      // over the header, outside the window, in another frame. Without that, `down` stays true for
      // ever and every mouse movement anywhere drags the row. One drag, page ruined.
      if (!movedAt) { try { el.setPointerCapture(e.pointerId); } catch { /* older engine: the buttons check above covers it */ } }
      movedAt = Date.now();
      // the seam is crossed mid-drag too: pull past either edge and the row rolls round instead of
      // hitting a wall, with the anchor moved by the same lap so the hand keeps the card it grabbed
      const lap = lapWidth();
      let next = startLeft - dx;
      if (lap > 0) {
        while (next < 0) { next += lap; startLeft += lap; }
        while (next >= lap) { next -= lap; startLeft -= lap; }
      }
      el.scrollLeft = next;
    };
    const end = () => {
      if (!down) return;
      down = false;
      el.style.cursor = '';
      // LET GO AND IT PICKS UP AGAIN (Marc: "cuando la sueltes, sigue el efecto"). Half a second, just
      // long enough not to yank the card out from under the eye that was following the hand.
      driftHold.current = Date.now() + 500;
    };
    // only the click that ENDS a drag is swallowed; 150 ms later the shelf takes clicks again
    const onClick = (e: MouseEvent) => { if (movedAt && Date.now() - movedAt < 150) { e.preventDefault(); e.stopPropagation(); } };
    // A HAND ON THE ROW stops the drift — a finger on the Tesla, the wheel — and it picks up again a beat
    // later. Only real gestures count: hovering used to hold it too, which meant the shelf froze whenever
    // the cursor merely crossed it and looked, correctly, broken.
    const hold = () => { driftHold.current = Date.now() + 1200; };
    // THE PICTURE WAS THE THIEF (Marc, 2026-09-02: "tengo que ser capaz de arrastrar el mouse y parar o
    // mover las fotos, ahora no puedo"). An <img> is draggable by default: three pixels into a mouse drag
    // the browser starts a native drag-and-drop of the PHOTO, fires pointercancel on us, and the drag is
    // over before the row has moved a pixel. Every card is a photo, so no card could ever be dragged.
    const onDragStart = (e: Event) => e.preventDefault();
    el.addEventListener('dragstart', onDragStart);
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('wheel', hold, { passive: true });
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointerdown', hold);
    el.addEventListener('touchstart', hold, { passive: true });
    el.addEventListener('touchmove', hold, { passive: true });
    window.addEventListener('pointermove', onMove);
    // pointerup AND mouseup: the pointer events are the modern pair, but not every engine (or embedded
    // browser — a car's is not a desktop Chrome) is guaranteed to deliver the pointer half of a release
    window.addEventListener('pointerup', end);
    window.addEventListener('mouseup', end);
    window.addEventListener('pointercancel', end);
    window.addEventListener('blur', end);
    el.addEventListener('click', onClick, true);
    return () => {
      el.removeEventListener('wheel', hold);
      el.removeEventListener('pointerdown', hold);
      el.removeEventListener('touchstart', hold);
      el.removeEventListener('touchmove', hold);
      el.removeEventListener('dragstart', onDragStart);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('mouseup', end);
      window.removeEventListener('pointercancel', end);
      window.removeEventListener('blur', end);
      el.removeEventListener('click', onClick, true);
    };
  }, []);
  // THE SHELF TURNS BY ITSELF (Marc: "auto scroll infinito horizontal, muy lento"). Slow enough to read
  // — 16 px a second, about ten seconds a card — and it never reaches an end because the row carries a
  // second copy of itself: at one lap travelled, scrollLeft drops back a lap onto an identical picture.
  // Snap has to go, or every card would yank the drift back into its slot.
  useEffect(() => {
    const el = carRow.current;
    if (!el) return;
    const still = matchMedia('(prefers-reduced-motion: reduce)');
    el.style.scrollSnapType = 'none';
    let raf = 0, last = 0, pos = el.scrollLeft;
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      const dt = last ? Math.min(0.05, (now - last) / 1000) : 0;
      last = now;
      const lap = lapWidth();
      // hands off, tab in front, motion welcome, and something to travel
      // `:active` is the browser's own word for "the mouse is still down on this row" (a pressed card
      // makes every ancestor :active): a press that HOLDS keeps the shelf still for as long as it holds,
      // not just for the 400 ms after the last movement, and the browser clears it on release itself,
      // so a missed pointerup can never freeze the drift the way a flag of our own could
      if (still.matches || document.hidden || choiceOpen.current || Date.now() < driftHold.current || el.matches(':active') || lap <= el.clientWidth) return;
      // the position is kept HERE, not read back from scrollLeft: a frame moves the row about a quarter
      // of a pixel, and a browser that hands scrollLeft back rounded would swallow every one of them and
      // the shelf would sit still for ever. Only a real jump (a drag, a swipe) resyncs us to the element.
      if (Math.abs(el.scrollLeft - pos) > 1.5) pos = el.scrollLeft;
      pos += 16 * dt;
      if (pos >= lap) pos -= lap;
      el.scrollLeft = pos;
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
    // no deps: the loop reads scrollWidth every frame, so a shelf that grows is picked up on its own —
    // and publicCars is not even declared yet this far up the component
  }, []);
  const [acctBusy, setAcctBusy] = useState<'' | 'deleting' | 'failed'>('');
  const [langSet, setLangSet] = useState(getLangSetting());
  const logOut = () => { clearAccountData(); setEmail(null); };
  const removeAccount = async () => {
    if (!confirm(t('deleteConfirm'))) return;
    setAcctBusy('deleting');
    const ok = await deleteAccount();
    if (ok) { clearAccountData(); setEmail(null); setAcctBusy(''); alert(t('deletedOk')); }
    else setAcctBusy('failed');
  };
  // back at the menu → the engine must go quiet (the session stays alive so
  // re-entering the car just shows START again)
  useEffect(() => {
    void currentSession()?.stopEngine();
  }, []);
  // THE ORDER OF THE SHELF (Marc, 2026-09-02). What the driver already owns comes first — they paid for
  // it, they should not have to ride the carousel round to find it — and everything else follows the
  // house order below. Anything not named falls in behind, in registry order.
  const CAR_ORDER = ['porsche-911-gt3-cup', 'lamborghini-aventador', 'ferrari-812', 'ferrari-458'];
  const rank = (car: CarProfile) => { const i = CAR_ORDER.indexOf(car.id); return i < 0 ? CAR_ORDER.length : i; };
  const owned = (car: CarProfile) => (car.available && isCarUnlocked(car.id) ? 0 : 1);
  const publicCars = cars
    .filter((car) => !car.adminOnly)
    .sort((a, b) => owned(a) - owned(b) || rank(a) - rank(b));
  const testCars = cars.filter((car) => car.adminOnly);
  const hasTestShelf = admin && testCars.length > 0;
  // the store's prices are asked for while the shelf is still being looked at, so the glass opens on
  // the price the store will actually charge instead of the web's, and never changes it under the eye
  useEffect(() => onIapReady(() => { for (const car of publicCars) if (car.available && !isCarUnlocked(car.id)) void iapPrice(car.id); }), []);
  const renderCar = (car: CarProfile, featured = false, dup = false) => {
    const unlocked = car.available && isCarUnlocked(car.id);
    const locked = car.available && !unlocked && !car.free;
    return (
      <li key={`${dup ? 'dup-' : ''}${car.id}`} class={featured ? 'carlist__item--featured' : ''} aria-hidden={dup || undefined}>
        <button
          class={`carcard ${car.photo ? 'carcard--photo' : ''} ${featured ? 'carcard--featured' : ''} ${!car.available ? 'carcard--soon' : ''} ${locked ? 'carcard--locked' : ''}`}
          tabIndex={dup ? -1 : undefined}
          disabled={!car.available}
          style={{ '--card-accent': car.hud?.brand ?? car.hud?.accent ?? 'transparent' } as Record<string, string>}
          onClick={(e) => (locked ? openChoice(car, e.currentTarget as HTMLElement) : navigate(`/garage/${carPath(car)}`))}
        >
          {car.photo && <img class="carcard__photo" src={car.photo.src} alt="" loading="lazy"
            style={car.photo.pos ? { objectPosition: car.photo.pos } : undefined} />}
          <div class="carcard__maker">{car.manufacturer}</div>
          <div class="carcard__name">{car.name}</div>
          <div class="carcard__engine">{car.engineType}</div>
          {!car.available && <div class="carcard__badge">{t('comingUp')}</div>}
          {unlocked && !admin && <div class="carcard__badge carcard__badge--owned">{car.free ? t('free') : t('owned')}</div>}
          {car.photo?.credit && <span class="carcard__credit">{car.photo.credit}</span>}
        </button>
      </li>
    );
  };
  const comingSoonCard = (dup = false) => (
    <li key={`${dup ? 'dup-' : ''}next`} class="carlist__item--next" aria-hidden={dup || undefined}>
      <div class="carcard carcard--soon carcard--next" aria-disabled="true">
        <div class="carcard__maker">{t('nextEngine')}</div>
        <div class="carcard__name">{t('comingSoon')}</div>
        <div class="carcard__engine">{t('comingSoonBody')}</div>
        <div class="carcard__badge">{t('comingUp')}</div>
      </div>
    </li>
  );
  // THE SHELF IS RENDERED TWICE. The row drifts for ever, and the only way to cross the seam without a
  // jump is to have the same cards waiting on the other side: when the drift has travelled the width of
  // one copy, scrollLeft drops back by exactly that width and the picture does not change. The second
  // copy is furniture — aria-hidden, out of the tab order — so a screen reader still reads four cars.
  const shelf = (dup: boolean) => [...publicCars.map((car, i) => renderCar(car, i === 0, dup)), comingSoonCard(dup)];
  return (
    <main class="home home--v4">
      <header class="topbar">
        <div class="home__identity">
          <div class="home__brand">
            <img class="brandmark brandmark--h" src="/logo.svg?v=1" alt="" />
            <Wordmark class="wordmark" />
          </div>
          <p class="tagline">{t('tagline')}</p>
        </div>
      </header>

      <section class="garage" aria-label="Engines">
        <div class="home__catalog">
            <div class={`carnav ${carNav.width >= 0.999 ? 'carnav--off' : ''}`} aria-hidden="true"><i style={{ left: `${carNav.left * 100}%`, width: `${carNav.width * 100}%` }} /></div>
            <ul class="carlist carlist--main" ref={carRow} onScroll={onCarScroll}>
              {shelf(false)}
              {shelf(true)}
            </ul>
            {hasTestShelf && (
              <>
                <div class="home__catalog-divider"><span>TEST · admin only</span></div>
                <ul class="carlist carlist--test">
                  {testCars.map((car) => renderCar(car))}
                </ul>
              </>
            )}
          </div>
      </section>

      <section class="landing">
        <div class="landing__section">
          <div class="eyebrow landing__eyebrow">{t('howItWorks')}</div>
          <div class="landing__steps">
            <div class="landing__step"><b>1</b><h3>{t('step1Title')}</h3><p>{t('step1Body')}</p></div>
            <div class="landing__step"><b>2</b><h3>{t('step2Title')}</h3><p>{t('step2Body')}</p></div>
            <div class="landing__step"><b>3</b><h3>{t('step3Title')}</h3><p>{t('step3Body')}</p></div>
          </div>
        </div>
        <div class="landing__section">
          <div class="eyebrow landing__eyebrow">{t('questions')}</div>
          <div class="landing__faq">
            <details><summary>{t('q1')}</summary><p>{t('a1')}</p></details>
            <details><summary>{t('q2')}</summary><p>{t('a2')}</p></details>
            <details><summary>{t('q3')}</summary><p>{t('a3')}</p></details>
            <details><summary>{t('q4')}</summary><p>{t('a4')}</p></details>
          </div>
        </div>
        <div class="landing__section landing__section--cta">
          <div class="reviewwall">
            <span class="reviewwall__stars" aria-hidden="true">★★★★★</span>
            <p class="reviewwall__line">{t('wallLine')}</p>
            <p class="reviewwall__how">{t('wallHow')} <b>hello@roardrive.com</b></p>
          </div>
        </div>
      </section>

      <footer class="home__foot">
        <span class="home__footlinks home__footlinks--legal">
          <span>© {new Date().getFullYear()} Fremmenn Dynamics, S.L.</span>
          <a href="/legal/terms">{t('terms')}</a>
          <a href="/legal/privacy">{t('privacy')}</a>
          <a href="/legal/cookies">{t('cookies')}</a>
          <a href="/legal/gdpr-rights">{t('gdpr')}</a>
          <a href="/legal/legal-notice">{t('legalNotice')}</a>
        </span>
        <span class="home__footlinks">
          {/* the build id and the diagnostics page are the desk's, not the driver's (Marc) */}
          {admin && <span>{typeof __ROAR_BUILD__ !== 'undefined' ? __ROAR_BUILD__ : 'dev'}</span>}
          <button class="home__acct" onClick={() => setShowAccount(true)}>{t('account')}</button>
          {admin && <a href="/admin">{t('admin')}</a>}
          {admin && <a href="/diag">{t('diagnostics')}</a>}
        </span>
      </footer>

      {createPortal(
        <button class={`menubtn ${menu && !closing ? 'menubtn--open' : ''}`} onClick={() => setMenu(!(menu && !closing))} aria-expanded={menu && !closing} aria-label="Menu">
          <span class="menubtn__lines"><i /><i /></span>
          <span class="menubtn__label">MENU</span>
          {email && <i class="menubtn__dot" title={email} />}
        </button>
        , document.body)}
      {menu && createPortal(
        <div class={`drawer ${closing ? 'drawer--closing' : ''}`} role="dialog" aria-label="Menu">
          <div class="drawer__scrim" onClick={() => setMenu(false)} />
          <aside class="drawer__panel">
            {/* the drawer is the MENU, and says so, level with the ✕ that closes it (Marc) */}
            <div class="drawer__top"><span class="drawer__brand">{inApp ? t('menu') : 'RoarDrive'}</span></div>
            <div class="drawer__body">
              {!email && <div class="drawer__section">
                <div class="eyebrow drawer__eyebrow">{t('signIn')}</div>
                <div class="acctpanel">
                  <p class="acctpanel__lead">{t('drawerSignInLead')}</p>
                  <p class="setup__note">{t('drawerSignInBody')}</p>
                  <button class="acctpanel__btn" onClick={() => { setMenu(false); setShowAccount(true); }}>{t('signIn')}</button>
                </div>
              </div>}
              {email && <div class="drawer__section">
                <div class="eyebrow drawer__eyebrow">{t('account')}</div>
                <div class="acctpanel">
                  {/* the address gets the whole line. LOG OUT used to sit on the same row and ate the
                      end of it (Marc), and an email you cannot read in full is a bad way to tell somebody
                      which account they are in. Both ways out live below, in one place: log out first,
                      delete second. */}
                  <div class="acctpanel__who"><i /><span>{email}</span></div>
                  <p class="setup__note">{t('drawerAccountBody')}</p>
                  {admin && <a class="pill pill--btn" href="/admin">{t('adminPanel')}</a>}
                  <details class="acctdanger">
                    <summary>{t('dangerZone')}</summary>
                    <button class="acctmenu__item" onClick={logOut}>{t('logOut')}</button>
                    <button class="acctmenu__item acctmenu__item--danger" disabled={acctBusy === 'deleting'} onClick={() => void removeAccount()}>{acctBusy === 'deleting' ? t('deleting') : t('deleteAccount')}</button>
                    {acctBusy === 'failed' && <div class="acctmenu__err">{t('deleteFailed')}</div>}
                  </details>
                </div>
              </div>}
              {/* the language lives under the account (Marc): automatic by default, following the phone */}
              <div class="drawer__section">
                <div class="eyebrow drawer__eyebrow">{t('language')}</div>
                <div class="langpick">
                  <button class={`langpick__opt ${langSet === null ? 'langpick__opt--on' : ''}`} onClick={() => { setLang(null); setLangSet(null); }}>
                    <b>{t('languageAuto')}</b><small>{t('languageAutoNote', { name: LANGS.find((l) => l.code === detectLang())?.name ?? 'English' })}</small>
                  </button>
                  {LANGS.map((l) => (
                    <button key={l.code} class={`langpick__opt ${langSet === l.code ? 'langpick__opt--on' : ''}`} onClick={() => { setLang(l.code); setLangSet(l.code); }}>
                      <b>{l.name}</b>{getLang() === l.code && langSet === null && <small>{t('languageAuto')}</small>}
                    </button>
                  ))}
                </div>
              </div>
              {/* Restore: Apple requires it to be findable without buying anything first, so it sits in
                  the menu at all times — signed in or not, in the app or in a browser. Last, under the language (Marc):
                  account, language, purchases, in the order they get touched */}
              <div class="drawer__section">
                <div class="eyebrow drawer__eyebrow">{t('purchases')}</div>
                <div class="acctpanel">
                  <p class="setup__note">{t('restoreNote')}</p>
                  <RestoreButton />
                </div>
              </div>
            </div>
          </aside>
        </div>,
        document.body,
      )}

      {showAccount && <AccountModal onClose={() => { setShowAccount(false); setEmail(getAccountEmail()); if (location.pathname === '/account') history.replaceState(null, '', '/'); }} />}
      {choice && <CarChoice profile={choice.car} anchor={choice.rect} onClose={() => setChoice(null)} />}
    </main>
  );
}
